import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crosshair, Megaphone, Wallet, Check, X } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { Field, inputCls, VoltButton, SuccessPanel } from '../components/site/FormBits';
import { SERVICES, COUNTRIES, SERVICE_GROUPS, DELIVERY_MODES } from '../data/site';
import { API, track } from '../lib/api';
import { useSEO } from '../lib/seo';

const props = [
  { icon: Crosshair, title: 'Pre-qualified RFQs', text: 'Every enquiry has scope, budget band and deadline attached. No tyre-kickers, no ambiguity.' },
  { icon: Megaphone, title: 'No cold outreach', text: 'Work comes to you. State your capability once; see only RFQs that fit your wheelhouse.' },
  { icon: Wallet, title: 'No payment during the pilot', text: 'You pay nothing now. Billing starts only when the full platform launches — founding members keep the locked SGD 29/mo rate for as long as their subscription stays active.' },
];

const plans = [
  { name: 'Starter', price: 'SGD 0', per: '', note: 'After launch', features: ['Listed profile', 'Browse public RFQs', 'Quote on 2 RFQs / month'], muted: true },
  { name: 'Founding', price: 'SGD 29', per: '/mo', note: 'Rate held while subscribed · first 30 only', features: ['Everything in Pro', 'Founding rate kept while subscribed', 'Priority matching during concierge', 'Founder direct line'], founding: true },
  { name: 'Pro', price: 'SGD 59', per: '/mo', note: 'Standard rate at launch', features: ['Unlimited matched RFQs', 'Verified badge', 'Category priority'], muted: true },
  { name: 'Premium', price: 'SGD 149', per: '/mo', note: 'Standard rate at launch', features: ['Everything in Pro', 'Multi-category coverage', 'Featured placement'], muted: true },
];

const emptyForm = { company_name: '', uen: '', contact_name: '', email: '', phone: '', country: 'SG' };

const Providers = () => {
  useSEO({
    title: 'For Providers — Founding Rate SGD 29/mo | Vox Meridian',
    description: 'Join as a founding provider: pre-qualified RFQs in your market, no cold outreach, and a private dashboard to track matches and quotes.',
    path: '/providers',
  });
  const [form, setForm] = useState(emptyForm);
  const [cats, setCats] = useState([]);
  const [serviceCountries, setServiceCountries] = useState(['SG']);
  const [deliveryModes, setDeliveryModes] = useState(['on_site']);
  const [group, setGroup] = useState(SERVICE_GROUPS[0].name);
  const [pendingCat, setPendingCat] = useState('');
  const groupServices = SERVICES.filter((s) =>
    (SERVICE_GROUPS.find((g) => g.name === group) || SERVICE_GROUPS[0]).trades.includes(s.name)
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCat = (slug) =>
    setCats((c) => (c.includes(slug) ? c.filter((x) => x !== slug) : [...c, slug]));
  const setBaseCountry = (e) => {
    const code = e.target.value;
    setForm({ ...form, country: code });
    setServiceCountries((current) => (current.length === 1 && current[0] === form.country ? [code] : current));
  };
  const toggleMode = (id) =>
    setDeliveryModes((modes) => (modes.includes(id) ? modes.filter((x) => x !== id) : [...modes, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (cats.length === 0) {
      toast.error('Select at least one service category.');
      return;
    }
    if (serviceCountries.length === 0) {
      toast.error('Select at least one market you serve.');
      return;
    }
    if (deliveryModes.length === 0) {
      toast.error('Select at least one delivery mode.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/providers`, {
        ...form,
        categories: cats,
        service_countries: serviceCountries,
        delivery_modes: deliveryModes,
      });
      track('conversion', { type: 'provider_signup' });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="providers-page">
      <PageHero
        eyebrow="For providers"
        lines={[<>Pre-qualified RFQs.</>, <span key="l2">Zero <span className="text-volt">cold outreach.</span></span>]}
        sub="Vox Meridian puts matched RFQs from your country in your inbox — with scope, budget and deadline attached. You quote on your terms and pay a flat rate only if the channel works for you."
      />

      <section className="border-b border-line bg-ink">
        <div className="mx-auto grid max-w-7xl sm:grid-cols-3">
          {props.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1} className={`border-line p-8 sm:p-10 ${i > 0 ? 'border-t sm:border-l sm:border-t-0' : ''}`}>
              <p.icon size={28} className="text-volt" />
              <h3 className="mt-4 font-display text-base font-bold text-parchment">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-b border-line bg-ink bg-grid">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Chapter no="01" label="Pricing preview" title="Founding rate: SGD 29/mo, locked." />
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-steel">
            Plans below are the launch pricing. Nothing is billed during the concierge phase — the
            founding rate simply locks in for the first 30 verified providers, and stays at SGD 29/mo
            for as long as the subscription remains active. Reserving a founding spot creates no
            obligation to subscribe later.
          </p>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <div
                  data-testid={`plan-${p.name.toLowerCase()}`}
                  className={`flex h-full flex-col border p-8 transition-colors duration-300 ${
                    p.founding ? 'border-volt bg-volt/5' : 'border-line bg-surface hover:border-steel/40'
                  }`}
                >
                  <p className={`mono-label ${p.founding ? 'text-volt' : 'text-steel'}`}>{p.name}</p>
                  <p className="mt-4 font-display text-4xl font-black text-parchment">
                    {p.price}<span className="text-lg text-steel">{p.per}</span>
                  </p>
                  <p className={`mt-1 font-mono text-xs ${p.founding ? 'text-volt' : 'text-steel/70'}`}>{p.note}</p>
                  <ul className="mt-6 flex-1 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-steel">
                        <Check size={15} weight="bold" className={`mt-0.5 shrink-0 ${p.founding ? 'text-volt' : 'text-steel/60'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink" id="apply">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter no="02" label="Apply now" title="Claim a founding spot." />
              <Reveal delay={0.1}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-steel">
                  Fill the form — company, business registration number, categories, contact. The
                  founder verifies every application personally and calls you within 24 hours. 30
                  spots, first come, verified in order.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              {done ? (
                <SuccessPanel
                  testId="provider-success"
                  title="Application received."
                  lines={[
                    'A confirmation email is on its way to you.',
                    'The founder will contact you within 24 hours to verify your business registration and lock your founding rate.',
                  ]}
                />
              ) : (
                <form data-testid="provider-form" onSubmit={submit} className="space-y-6 border border-line bg-surface p-8 sm:p-10">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Company name" htmlFor="p-company">
                      <input id="p-company" data-testid="provider-company-input" required className={inputCls} value={form.company_name} onChange={set('company_name')} placeholder="Acme Engineering Pte Ltd" />
                    </Field>
                    <Field label="Business registration no." htmlFor="p-uen" hint="ACRA UEN (SG) · SSM no. (MY) · trade licence (UAE) · CRN (UK) · ABN (AU)…">
                      <input id="p-uen" data-testid="provider-uen-input" required className={inputCls} value={form.uen} onChange={set('uen')} placeholder="e.g. 202301234567" />
                    </Field>
                    <Field label="Country" htmlFor="p-country">
                      <select id="p-country" data-testid="provider-country-select" className={inputCls} value={form.country} onChange={setBaseCountry}>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Markets you serve" htmlFor="p-markets" hint="Your base country is included automatically. Add other markets only if you can genuinely deliver there.">
                    <select
                      id="p-markets"
                      data-testid="provider-market-add-select"
                      className={inputCls}
                      value=""
                      onChange={(e) => {
                        const code = e.target.value;
                        if (code && !serviceCountries.includes(code)) setServiceCountries([...serviceCountries, code]);
                      }}
                    >
                      <option value="">Add a market</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code} disabled={serviceCountries.includes(c.code)}>{c.name}</option>
                      ))}
                    </select>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="provider-service-countries">
                      {serviceCountries.map((code) => {
                        const country = COUNTRIES.find((c) => c.code === code);
                        return (
                          <button
                            type="button"
                            key={code}
                            data-testid={`provider-market-${code.toLowerCase()}`}
                            onClick={() => setServiceCountries(serviceCountries.filter((c) => c !== code))}
                            className="flex items-center gap-1.5 border border-volt bg-volt/10 px-3 py-1.5 text-xs text-volt transition-colors duration-200 hover:bg-volt/20"
                          >
                            {country ? country.name : code}
                            <X size={12} weight="bold" />
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="Delivery modes" htmlFor="p-delivery" hint="Cross-border RFQs match automatically only when the market and delivery mode both fit.">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="provider-delivery-modes">
                      {DELIVERY_MODES.map((mode) => (
                        <button
                          type="button"
                          key={mode.id}
                          data-testid={`provider-delivery-${mode.id.replace(/_/g, '-')}`}
                          onClick={() => toggleMode(mode.id)}
                          className={`border px-3 py-2.5 text-left text-xs transition-colors duration-200 ${
                            deliveryModes.includes(mode.id)
                              ? 'border-volt bg-volt/10 text-volt'
                              : 'border-line text-steel hover:border-steel/50 hover:text-parchment'
                          }`}
                        >
                          {mode.name}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Service categories" htmlFor="p-group" hint="Pick a sector, then add every service you cover — 65 services across 6 sectors.">
                    <select
                      id="p-group"
                      data-testid="provider-group-select"
                      className={`${inputCls} mb-3`}
                      value={group}
                      onChange={(e) => { setGroup(e.target.value); setPendingCat(''); }}
                    >
                      {SERVICE_GROUPS.map((g) => (
                        <option key={g.name} value={g.name}>{g.name}</option>
                      ))}
                    </select>
                    <select
                      id="p-cats"
                      data-testid="provider-category-select"
                      aria-label="Sub-service"
                      className={inputCls}
                      value={pendingCat}
                      onChange={(e) => {
                        const name = e.target.value;
                        if (name && !cats.includes(name)) setCats([...cats, name]);
                        setPendingCat('');
                      }}
                    >
                      <option value="" disabled>Select sub-service</option>
                      {groupServices.map((s) => (
                        <option key={s.name} value={s.name} disabled={cats.includes(s.name)} label={`${s.name} — ${s.examples}`} />
                      ))}
                    </select>
                    {cats.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="provider-category-chips">
                        {cats.map((name) => {
                          const sid = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                          return (
                            <button
                              type="button"
                              key={sid}
                              data-testid={`provider-cat-${sid}`}
                              onClick={() => toggleCat(name)}
                              className="flex items-center gap-1.5 border border-volt bg-volt/10 px-3 py-1.5 text-xs text-volt transition-colors duration-200 hover:bg-volt/20"
                            >
                              {name}
                              <X size={12} weight="bold" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {cats.length > 0 && (
                      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-volt" data-testid="provider-selected-count">
                        Selected: {cats.length} {cats.length === 1 ? 'service' : 'services'} — switch sectors to add more
                      </p>
                    )}
                  </Field>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Contact person" htmlFor="p-name">
                      <input id="p-name" data-testid="provider-name-input" required className={inputCls} value={form.contact_name} onChange={set('contact_name')} placeholder="Full name" />
                    </Field>
                    <Field label="Phone" htmlFor="p-phone">
                      <input id="p-phone" data-testid="provider-phone-input" required className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+65 9123 4567" />
                    </Field>
                  </div>
                  <Field label="Work email" htmlFor="p-email">
                    <input id="p-email" data-testid="provider-email-input" required type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@company.sg" />
                  </Field>
                  <VoltButton type="submit" data-testid="provider-submit-button" disabled={submitting} className="w-full">
                    {submitting ? 'Submitting…' : 'Apply as founding provider'}
                  </VoltButton>
                  <p className="text-center font-mono text-xs text-steel/60">No payment taken · No obligation to subscribe later · Founder follows up within 24h.</p>
                  <p className="text-center text-xs text-steel/50">
                    Your details are used only to verify and onboard you.{' '}
                    <a href="/privacy" data-testid="provider-privacy-link" className="underline transition-colors duration-200 hover:text-volt">Privacy</a>
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Providers;
